// ── 1. BOOKING API · Laravel `/api/egift/*` ────────────────────────────────────────────────
// Decides whether a booking happens. Frozen; shapes follow plan/redeem-laravel-api.md.

export type IGiftStatus = 'can_use' | 'used' | 'expired' | 'void' | 'not_found'

export type IRedeemStage = 'verify' | 'booking' | 'confirm'

/** api §6 — `BranchInfo::getBranchList()` */
export interface IBranchOption {
  branch_id: number
  branch_name: string // short code, e.g. "OS_BKK_SL"
  branch_long_name: string
  branch_address: string
  branch_phone: string
  branchlong_desc: string // HTML
  map_link: string
  city_id: number
  city_name: string
  branch_open_time: string // HH:MM:SS
  branch_close_time: string // HH:MM:SS
}

/** api §6 — `PTimer::getTimeList()`. Only :00 and :30 minutes. */
export interface ITimeSlot {
  time_id: number
  time_start: string // HH:MM
}

export interface IRedeemFilter {
  branch: IBranchOption[]
  time: ITimeSlot[]
}

/** api §4 Branch A — the `d` object */
export interface IGiftDetail {
  gift_number: string
  expired: string // YYYY-MM-DD
  receive_from: string
  product: string // may contain HTML entities — decode before display
}

/** api §4 Branch B — booking shown for an already-used gift. `gift_used` is a string here, not an array. */
export interface IUsedGiftBooking {
  book_id: number
  bpds_id: number
  branch_id: number
  branch_long_name: string
  appt_date: string // YYYY-MM-DD
  appt_time: string // HH:MM:SS
  customer_name: string
  customer_phone: string
  gift_used: string
}

export interface IVerifyRequest {
  giftFrom: string
  giftNumber: string
}

/** api §4. Envelope is `{ status, data }`, not `{ success, data }`. `failure` = business rejection. */
export type IVerifyResponse =
  | {
      status: 'success'
      data: {
        gift_status: 'can_use'
        token: string // JWT, 60-min TTL, opaque to us (SEC-003)
        d: IGiftDetail
        filter: IRedeemFilter
      }
    }
  | {
      status: 'failure'
      data: { gift_status: 'used'; detail: IUsedGiftBooking }
    }
  | {
      status: 'failure'
      data: { gift_status: Exclude<IGiftStatus, 'can_use' | 'used'> }
    }
  | { status: 'error'; msg: string; detail?: string }

/** api §3 */
export type IFilterResponse =
  | { status: 'success'; data: IRedeemFilter }
  | { status: 'error'; msg: string; detail?: string }

/** api §5 request. `yourPhone` must be a string — never `{ dialCode, value }`. */
export interface IBookingRequest {
  yourName: string
  yourEmail: string
  yourPhone: string
  remark?: string
  city: number
  branch: number
  date: string // YYYY-MM-DD
  time: string // HH:MM
  hours: string // H:MM
  guest: 1 | 2
  source?: string
  /** raw `utm_content` from the marketing link; SMS stores it as-is, capped at 10 chars */
  utm_content?: string
  timevalue?: string
  token?: string
}

/** api §5 happy path — `InsertBooking::insertBooking()` return dict. */
export interface IBookingDetail {
  book_id: number
  bpds_id: number
  appt_date: string // YYYY-MM-DD
  appt_time_id: number
  appt_time: string // HH:MM:SS
  total_hours: string // HH:MM:SS
  branch_id: number
  branch_name: string // this is the branch_long_name value
  qty_people: number
  voucher_no: string | null
  channel_id: number
  channel_name: string
  cust_name: string
  cust_phone: string
  cust_email: string
  total_amount: number // 0 for gift-redeem
  gift_used: Array<{ gift_number: string; product: string; expired: string }>
  transport: { pickup: Record<string, unknown>; takeback: Record<string, unknown> }
  products: unknown[]
  created_order_at: string // YYYY-MM-DD HH:mm:ss
  remind_rsvn: boolean
  // Appended by the controller (L221-223), not by InsertBooking:
  map_link: string
  branch_address: string
  branchlong_desc: string
}

/**
 * api §5 — `Appointment::getRecommend` documents ONE branch object, or `[]` for none. Typed wider:
 * both controller paths build it from a Collection. Read with `pickRecommendBranch()`.
 */
export interface IBookingRecommend {
  branch: IBranchOption | IBranchOption[]
  time: string[] // HH:MM:SS
}

/**
 * The branch to offer, or null. Takes a bare object, a list, or a string `branch_id` — PDO
 * emulated prepares stringify MySQL ints, which would otherwise read as "no recommendation".
 */
export function pickRecommendBranch(
  branch: IBookingRecommend['branch']
): { branch_id: number; branch_long_name?: string } | null {
  const first = Array.isArray(branch) ? branch[0] : branch
  const branchId = Number(first?.branch_id)

  if (!Number.isFinite(branchId) || branchId <= 0) return null

  return { branch_id: branchId, branch_long_name: first?.branch_long_name }
}

export type IBookingResponse =
  | { status: 'success'; data: { create_status: true; detail: IBookingDetail } }
  | { status: 'success'; data: { create_status: false; recommend: IBookingRecommend } }
  // A gift that turns used/void/expired between verify and booking returns a failed-verify shape.
  | { status: 'failure'; data: { gift_status: IGiftStatus } }
  | { status: 'error'; msg: string; detail?: string; line?: number }

// ── 2. AVAILABILITY HINT · Destiny TH `/api/room-availability/checkavailable` ───────────────
// Advisory. Nothing here stops a booking on its own — section 1 decides.

/**
 * Sent with `parity_mode: 'redeem'` so it answers under the booking API's rules, not the SMS desk's.
 * Contract: API_hub_test/src/apis/sms/v1/room-availability/room-availability.dto.ts (old repo name;
 * the deployed service is Destiny TH).
 */
export interface ICheckAvailabilityRequest {
  appointment_date: string // YYYY-MM-DD
  appointment_time: string // HH:MM
  total_hour: string[] // H:MM per guest; the API uses the longest
  branch_id: number
  guest: number
  INT_recommend: boolean
  parity_mode: 'redeem'
  /** The gift's redeemable branches, so cross-branch suggestions stay inside them. */
  allowed_branch_ids: number[]
}

/** Why the API said no. `message` is English prose, so this is what drives the copy. */
export type IAvailabilityReason =
  | 'PAST'
  | 'ADVANCE_WINDOW'
  | 'OUTSIDE_HOURS'
  | 'ROOM_BUSY'
  | 'CAPACITY'
  | 'GIFT_CAP'
  | 'INVALID_INPUT'

export interface IAvailabilityAlternativeBranch {
  branch_id: number
  branch_name2: string
  times: string[] // HH:MM
}

/** Every field is optional — the API omits most of them on the happy path. */
export interface ICheckAvailabilityResponse {
  roomAvailable?: boolean
  message?: string
  reason?: IAvailabilityReason
  branch_id?: number
  branch_name2?: string
  recommendTime?: boolean
  recommendedTimes?: string[] // HH:MM, at the requested branch
  alternativeBranches?: IAvailabilityAlternativeBranch[]
  statusCode?: number
}

// ── 3. VIEW MODELS · ours, not any API's ───────────────────────────────────────────────────
// utils/recommend.ts normalises both suggestion sources into these, so the panel never learns
// which one answered.

/** What `RecommendPanel` renders — same shape whichever source answered. */
export interface IRecommendOption {
  branchId: number
  branchName: string
  /** True when this is the branch the user already chose, which reads differently in the panel. */
  isCurrentBranch: boolean
  times: string[] // HH:MM
}

export interface IRedeemRecommend {
  reason: IAvailabilityReason | null
  options: IRecommendOption[]
}

// ── 4. LEGACY PHP · sendmail.php, and the query shapes the old page minted ──────────────────

/** migration-spec §3 — the 17 fields `sendmail.php` expects. */
export interface ISendMailRequest {
  bpds_id: number | string
  customer_name: string
  customer_phone: string
  customer_email: string
  guest: number
  appt_date: string
  appt_time: string
  total_hours: string
  branch_long_name: string
  branchlong_desc: string
  map_link: string
  branch_address: string
  remark: string
  gift_number: string
  gift_product: string
  gift_expired: string
  redeem_at: string
}

/** migration-spec §1 — the only two fields any `?token=` deeplink has ever carried. */
export interface IRedeemDeeplink {
  giftFrom: string
  giftNumber: string
}

/** migration-spec §1 — the secondary, non-token prefill path. */
export interface IRedeemQueryPrefill {
  date?: string
  time?: string
  branch?: string
  guest?: string
}

/**
 * `network` and `booking-unconfirmed` both arrive as `{ status: 'error' }` but need opposite advice:
 * `network` never landed, so retry. `booking-unconfirmed` threw DURING createBooking, whose rollback
 * is manual — a retry can burn the eGift.
 */
export type IRedeemErrorVariant =
  | IGiftStatus
  | 'network'
  | 'booking-unconfirmed'
  | 'rate-limited'
  | 'captcha-fail'
